import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';
import DirectoryMap from './DirectoryMap';
import NetworkTab from './NetworkTab';
import { Map, Network, X, Video } from 'lucide-react';
import { PROVIDER_TYPES } from '../../lib/pipStates';

const TYPE_LABELS = {
  law_firm: 'Law Firm',
  marketer: 'Marketer',
  medical_practice: 'Medical Practice',
};

// Same palette as the admin map (AdminMapPage.typeColor) — the two maps
// must read identically for users and admins.
const TYPE_COLORS = {
  law_firm: '#8b5cf6',
  marketer: '#a78bfa',
  medical_practice: '#0DA7CA',
};

export default function DirectoryPage() {
  const { currentOrg } = useOrg();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [providerTypeFilter, setProviderTypeFilter] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [activeTab, setActiveTab] = useState('map');
  const navigate = useNavigate();
  const loc = useLocation();
  const basePath = loc.pathname.startsWith('/client') ? '/client/directory' : '/app/directory';
  const [mobileView, setMobileView] = useState('list');
  const [userLocation, setUserLocation] = useState(null);
  const [locationFilter, setLocationFilter] = useState('');
  const [nearMe, setNearMe] = useState(false);
  const [searchLocation, setSearchLocation] = useState(null); // { lat, lng, label }
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchDebounce = React.useRef(null);
  const isClientView = loc.pathname.startsWith('/client');
  const NEAR_ME_RADIUS = 15; // miles
  const [searchRadius, setSearchRadius] = useState(15); // miles, customizable

  // Get user's location for "Near Me" sorting
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {} // silently fail
      );
    }
  }, []);

  const fetchOrgs = useCallback(async () => {
    try {
      const data = await api.getDirectory({}, currentOrg?.id);
      setOrgs(data);
    } catch (err) {
      console.error('Failed to fetch directory:', err);
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);


  // Close suggestions on outside click
  React.useEffect(() => {
    const handler = () => setShowSuggestions(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Location search — geocode via Mapbox
  const handleSearchChange = (val) => {
    setSearch(val);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);

    if (val.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      if (!val) { setSearchLocation(null); setNearMe(false); setLocationFilter(''); }
      return;
    }

    searchDebounce.current = setTimeout(async () => {
      const token = process.env.REACT_APP_MAPBOX_TOKEN;
      if (!token) return;
      try {
        const res = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(val)}.json?access_token=${token}&country=us&types=place,postcode,region,locality&limit=5`
        );
        const data = await res.json();
        const results = (data.features || []).map(f => {
          const types = f.place_type || [];
          // For a state (region), grab the 2-letter code e.g. US-TX -> TX
          const shortCode = f.properties?.short_code || '';
          const stateAbbrev = types.includes('region') && shortCode.toUpperCase().startsWith('US-')
            ? shortCode.split('-')[1].toUpperCase()
            : null;
          const ctxRegion = (f.context || []).find(c => String(c.id || '').startsWith('region'));
          const ctxCode = String(ctxRegion?.short_code || '').toUpperCase();
          return {
            id: f.id,
            label: f.place_name,
            lat: f.center[1],
            lng: f.center[0],
            placeType: types[0] || null,
            stateAbbrev: stateAbbrev || (ctxCode.startsWith('US-') ? ctxCode.slice(3, 5) : null),
          };
        });
        setSearchSuggestions(results);
        setShowSuggestions(results.length > 0);
      } catch {
        setSearchSuggestions([]);
      }
    }, 300);
  };

  const selectSearchLocation = (suggestion) => {
    setSearch(suggestion.label);
    // State selection: filter by state abbreviation instead of radius, clear searchLocation
    if (suggestion.placeType === 'region' && suggestion.stateAbbrev) {
      setLocationFilter(suggestion.stateAbbrev);
      setSearchLocation({
        lat: suggestion.lat,
        lng: suggestion.lng,
        label: suggestion.label,
        isState: true,
      });
    } else {
      setSearchLocation({
        lat: suggestion.lat,
        lng: suggestion.lng,
        label: suggestion.label,
        stateAbbrev: suggestion.stateAbbrev || null,
      });
      setLocationFilter('');
    }
    setShowSuggestions(false);
    setSearchSuggestions([]);
    setNearMe(false);
  };

  const clearSearch = () => {
    setSearch('');
    setSearchLocation(null);
    setSearchSuggestions([]);
    setShowSuggestions(false);
  };

  // Haversine distance in miles
  const getDistance = (lat1, lng1, lat2, lng2) => {
    const R = 3959;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // Determine the reference point for distance calc
  const refPoint = searchLocation || (nearMe ? userLocation : userLocation);

  // Each geocoded location is findable in its own right — a firm searching
  // "Fort Myers" should hit the facility there, not just its parent group.
  // Orgs without locations list as themselves, unchanged.
  const listable = orgs.flatMap(o => {
    const locs = (o.locations || []).filter(l => !l.archived_at);
    // Serves whole states by video (set by the platform, 071). Listed for
    // those states and shown ahead of distance results, never by miles.
    const tele = (o.telehealth_states || []).map(s => s.toUpperCase());
    if (!locs.length) return [{ ...o, _tele: tele }];
    return locs.map(l => ({
      ...o,
      _tele: tele,
      id: `${o.id}::${l.id}`,          // unique key for selection/render
      _orgId: o.id,                     // real org for navigation
      _parentName: o.name,
      _isLocation: true,
      name: l.name || o.name,
      address_line1: l.address_line1 || o.address_line1,
      city: l.city || o.city,
      state: l.state || o.state,
      zip: l.zip || o.zip,
      phone: l.phone || o.phone,
      lat: l.lat ?? o.lat,
      lng: l.lng ?? o.lng,
    }));
  });

  const withDistance = listable.filter(o => {
    // Patients only browse medical practices — law firms are staff-only
    if (isClientView && o.type !== 'medical_practice') return false;
    if (typeFilter && o.type !== typeFilter) return false;
    if (providerTypeFilter && o.provider_type !== providerTypeFilter) return false;
    if (locationFilter && o.state?.toUpperCase() !== locationFilter && !(o._tele || []).includes(locationFilter)) return false;
    // If no search location set, do text-based name filter
    if (search && !searchLocation) {
      const term = search.toLowerCase();
      if (!(o.name?.toLowerCase().includes(term) || o._parentName?.toLowerCase().includes(term) || o.city?.toLowerCase().includes(term) || o.state?.toLowerCase().includes(term) || o.specialties?.some(s => s.toLowerCase().includes(term)))) return false;
    }
    return true;
  }).map(o => {
    // Attach distance from reference point
    if (refPoint && o.lat && o.lng) {
      return { ...o, _distance: getDistance(refPoint.lat, refPoint.lng, o.lat, o.lng) };
    }
    return o;
  });
  // Radius searches: a statewide video practice counts as "near" when the
  // search is in a state it serves. The searched state comes from the
  // suggestion when known, else from the in-radius results themselves.
  const radiusActive = (searchLocation && !searchLocation.isState) || (nearMe && userLocation);
  const radius = searchLocation ? searchRadius : NEAR_ME_RADIUS;
  const inRadius = (o) => o._distance != null && o._distance <= radius;
  const searchedStates = new Set([searchLocation?.stateAbbrev, ...(radiusActive ? withDistance.filter(inRadius).map(o => o.state?.toUpperCase()) : [])].filter(Boolean));
  const servesSearchedState = (o) => (o._tele || []).some(st => searchedStates.has(st));
  const filtered = withDistance.filter(o => {
    // State-level search: match on state abbreviation, not radius
    if (searchLocation?.isState) return true; // locationFilter already applied above
    if (radiusActive) return inRadius(o) || servesSearchedState(o);
    return true;
  }).map(o => (radiusActive && servesSearchedState(o) && !inRadius(o) ? { ...o, _statewide: true, _distance: null } : (o._tele?.length && (locationFilter ? o._tele.includes(locationFilter) : true) && !inRadius(o) ? { ...o, _statewide: true } : o)))
  .sort((a, b) => {
    // Statewide video practices lead the list, then by distance
    if (!!a._statewide !== !!b._statewide) return a._statewide ? -1 : 1;
    // Sort by distance when a reference point exists
    if (searchLocation || nearMe || userLocation) {
      if (a._distance != null && b._distance != null) return a._distance - b._distance;
      if (a._distance != null) return -1;
      if (b._distance != null) return 1;
    }
    return (a.name || '').localeCompare(b.name || '');
  });

  // The map pins locations itself, so feed it the parent orgs behind the
  // visible list (passing expanded entries would duplicate every pin).
  const visibleOrgIds = new Set(filtered.map(o => o._orgId || o.id));
  const mapOrgs = orgs.filter(o => visibleOrgIds.has(o.id));

  // Unique states for location filter
  const availableStates = [...new Set(
    orgs
      .filter(o => !isClientView || o.type === 'medical_practice')
      .map(o => o.state?.toUpperCase())
      .filter(Boolean)
  )].sort();

  const inputClass = 'w-full px-3 py-2.5 bg-tertiary border border-strong rounded-lg text-primary-c text-[12px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.12)]';

  const partnershipButton = (org) => {
    if (!currentOrg || org.id === currentOrg.id) return null;
    const p = org.partnership;
    if (p?.partnership_status === 'accepted') return <span className="text-[10px] font-semibold text-success px-2 py-0.5 rounded-full bg-success-soft">Connected</span>;
    if (p?.partnership_status === 'pending') return <span className="text-[10px] font-semibold text-warning px-2 py-0.5 rounded-full bg-warning-soft">Pending</span>;
    return null;
  };

  // ── Shared card components ──

  const CompactCard = ({ org }) => {
    const color = TYPE_COLORS[org.type] || '#6B7280';
    const initials = org.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
    const location = [org.city, org.state].filter(Boolean).join(', ');
    const isSelected = selectedOrg?.id === org.id;
    return (
      <button
        onClick={() => setSelectedOrg(isSelected ? null : org)}
        className={`w-full text-left flex items-center gap-2.5 p-2.5 rounded-lg mb-0.5 transition-all border ${isSelected ? 'bg-accent-glow border-accent-light/30 shadow-glow' : 'border-transparent hover:bg-hover-surface'}`}
      >
        {org.logo_url
          ? <img src={org.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
          : <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ background: color }}>{initials}</div>
        }
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-semibold text-primary-c truncate">{org.name}</div>
          <div className="text-[10px] text-muted-c truncate">{location || TYPE_LABELS[org.type]}</div>
          {org._statewide && (
            <div className="text-[10px] font-semibold text-accent-light inline-flex items-center gap-1"><Video size={10} /> Video visits anywhere in {org._tele.join(', ')}</div>
          )}
          {org._isLocation && (
            <div className="text-[10px] text-muted-c/70 truncate">part of {org._parentName}</div>
          )}
          {isSelected && (
            <button onClick={(e) => { e.stopPropagation(); navigate(`${basePath}/${org._orgId || org.id}`); }} className="text-[10px] font-semibold text-accent-light hover:underline mt-0.5">View Profile</button>
          )}
        </div>
        {partnershipButton(org) || <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />}
      </button>
    );
  };

  const MobileCard = ({ org }) => {
    const color = TYPE_COLORS[org.type] || '#6B7280';
    const initials = org.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
    const cityState = [org.city, org.state].filter(Boolean).join(', ');
    const specs = (org.specialties || []).slice(0, 3);
    const distLabel = org._statewide
      ? `video visits anywhere in ${org._tele.join(', ')}`
      : org._distance != null ? (org._distance < 1 ? '< 1 mi away' : `${Math.round(org._distance)} mi away`) : null;

    return (
      <button
        onClick={() => navigate(`${basePath}/${org._orgId || org.id}${isClientView && org.type === 'medical_practice' ? '?tab=book' : ''}`)}
        className="w-full text-left bg-elevated rounded-[16px] border border-theme overflow-hidden active:scale-[0.985] transition-all shadow-sm hover:shadow-md"
      >
        {/* Gradient accent strip */}
        <div className="h-1" style={{ background: `linear-gradient(90deg, ${color}, ${color}88)` }} />

        <div className="p-4 flex gap-3.5">
          {/* Avatar */}
          {org.logo_url ? (
            <img src={org.logo_url} alt="" className="w-14 h-14 rounded-[14px] object-cover flex-shrink-0 border border-theme" />
          ) : (
            <div className="w-14 h-14 rounded-[14px] flex items-center justify-center text-[16px] font-bold text-white flex-shrink-0" style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}>
              {initials}
            </div>
          )}

          {/* Content */}
          <div className="min-w-0 flex-1">
            {/* Name row */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[15px] font-bold text-primary-c truncate leading-tight">{org.name}</div>
                {org._isLocation && (
                  <div className="text-[11px] text-muted-c truncate">part of {org._parentName}</div>
                )}
                <div className="text-[12px] text-muted-c mt-0.5 flex items-center gap-1.5">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="flex-shrink-0 opacity-50"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  <span>{cityState}</span>
                  {distLabel && <><span className="opacity-30">·</span><span className="font-medium text-accent-light">{distLabel}</span></>}
                </div>
              </div>
              {/* Type badge */}
              <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md flex-shrink-0 mt-0.5" style={{ color, background: `${color}15` }}>
                {org.type === 'medical_practice' ? 'Medical' : org.type === 'law_firm' ? 'Law' : org.type === 'marketer' ? 'Marketer' : 'Other'}
              </span>
            </div>

            {/* Description preview */}
            {org.description && (
              <p className="text-[11px] text-secondary-c leading-relaxed mt-2 line-clamp-2">{org.description}</p>
            )}

            {/* Specialties */}
            {specs.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {specs.map(s => (
                  <span key={s} className="text-[10px] font-medium px-2 py-[3px] rounded-md bg-tertiary text-secondary-c border border-strong">{s}</span>
                ))}
                {(org.specialties || []).length > 3 && (
                  <span className="text-[10px] font-medium px-2 py-[3px] rounded-md text-muted-c">+{org.specialties.length - 3}</span>
                )}
              </div>
            )}

            {/* Footer row */}
            <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-theme">
              {org.type === 'medical_practice' ? (
                <span className="text-[12px] font-semibold text-accent-light flex items-center gap-1.5">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  Book Appointment
                </span>
              ) : (
                <span className="text-[11px] text-muted-c">{org.phone || ''}</span>
              )}
              <span className="text-[11px] font-medium text-muted-c">View Profile &rarr;</span>
            </div>
          </div>
        </div>
      </button>
    );
  };

  // ── Map instance (shared between desktop and mobile, never remounted) ──

  // Focus point for the map: explicit searchLocation wins, then Near Me, else none
  const mapFocus = searchLocation
    ? {
        lat: searchLocation.lat,
        lng: searchLocation.lng,
        zoom: searchLocation.isState
          ? 6
          : searchRadius <= 10 ? 11 : searchRadius <= 25 ? 10 : 9,
      }
    : nearMe && userLocation
      ? { lat: userLocation.lat, lng: userLocation.lng, zoom: 11 }
      : null;

  const mapElement = !loading && (
    <div className="relative w-full h-full">
      <DirectoryMap
        orgs={mapOrgs}
        selectedOrg={selectedOrg}
        onSelectOrg={setSelectedOrg}
        typeColors={TYPE_COLORS}
        typeLabels={TYPE_LABELS}
        currentOrgId={currentOrg?.id}
        currentOrgType={currentOrg?.type}
        onReferCase={(orgId) => navigate(`/app/referrals/concierge?provider=${orgId}&return=${encodeURIComponent('/app/directory')}`)}
        onViewProfile={(orgId, tab) => navigate(`${basePath}/${orgId}${tab ? '?tab=' + tab : ''}`)}
        isClientView={isClientView}
        focusLocation={mapFocus}
      />
      {/* Legend — same visual language as the admin map */}
      <div className="absolute bottom-3 left-3 z-10 flex items-center gap-3 px-3 py-1.5 rounded-[10px] bg-elevated/90 border border-theme text-[10.5px] text-muted-c backdrop-blur-sm pointer-events-none">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-[10px] h-[10px] rounded-full border-2 border-white shadow" style={{ background: TYPE_COLORS.medical_practice }} />
          Medical
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-[10px] h-[10px] rounded-full border-2 border-white shadow" style={{ background: TYPE_COLORS.law_firm }} />
          Law firm
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-[9px] h-[9px] rounded-full bg-white shadow" style={{ border: `2.5px solid ${TYPE_COLORS.medical_practice}` }} />
          Location
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center px-1 rounded-full text-[8.5px] font-bold text-white" style={{ background: '#6b7280', minWidth: 13, height: 13 }}>2</span>
          same address
        </span>
      </div>
    </div>
  );

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">

      {/* ════════════ DESKTOP ════════════ */}
      <div className="hidden md:flex flex-1 overflow-hidden">
        <div className="flex-1 relative">
          {activeTab === 'map' ? (
            loading
              ? <div className="absolute inset-0 bg-primary flex items-center justify-center"><div className="w-8 h-8 border-2 border-strong border-t-accent rounded-full animate-spin" /></div>
              : mapElement
          ) : <NetworkTab />}
        </div>

        <div className="w-[300px] bg-secondary border-l border-theme flex flex-col flex-shrink-0">
          <div className="flex border-b border-theme">
            {[{ key: 'map', label: 'Map', Icon: Map }, { key: 'network', label: 'Network', Icon: Network }].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex-1 py-3 text-[12px] font-semibold transition-all border-b-2 flex items-center justify-center gap-1.5 ${activeTab === tab.key ? 'text-accent-light border-accent' : 'text-muted-c border-transparent hover:text-primary-c'}`}>
                <tab.Icon size={14} strokeWidth={2.2} />
                {tab.label}
              </button>
            ))}
          </div>
          {activeTab === 'map' && (
            <>
              <div className="px-4 pt-4 pb-3 space-y-2.5">
                <div className="relative">
                  <input type="text" className={inputClass + ' pr-8'} placeholder="City, state, or zip..." value={search} onChange={e => handleSearchChange(e.target.value)} onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)} />
                  {search && (
                    <button onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-c hover:text-primary-c"><X size={12} strokeWidth={2.5} /></button>
                  )}
                  {showSuggestions && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-elevated border border-theme rounded-xl shadow-2xl z-50 overflow-hidden">
                      {searchSuggestions.map(s => (
                        <button key={s.id} onClick={() => selectSearchLocation(s)} className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-hover-surface text-[12px] text-primary-c border-b border-theme last:border-0">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted-c flex-shrink-0"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                          <span className="truncate">{s.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {searchLocation && (
                  <div className="flex items-center gap-2 px-1">
                    <span className="text-[10px] text-accent-light font-medium">Within</span>
                    <select value={searchRadius} onChange={e => setSearchRadius(Number(e.target.value))} className="text-[10px] font-semibold text-accent-light bg-transparent border-none focus:outline-none cursor-pointer">
                      {[5, 10, 15, 25, 50, 100].map(r => <option key={r} value={r}>{r} mi</option>)}
                    </select>
                    <span className="text-[10px] text-accent-light font-medium">of {searchLocation.label.split(',')[0]}</span>
                  </div>
                )}
                {!isClientView && (
                  <select className={inputClass} value={typeFilter} onChange={e => { setTypeFilter(e.target.value); if (e.target.value === 'law_firm') setProviderTypeFilter(''); }}>
                    <option value="">All types ({filtered.length})</option>
                    {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                )}
                {typeFilter !== 'law_firm' && (
                  <select className={inputClass} value={providerTypeFilter} onChange={e => setProviderTypeFilter(e.target.value)}>
                    <option value="">All provider types</option>
                    {PROVIDER_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
                  </select>
                )}
                {availableStates.length > 1 && (
                  <select className={inputClass} value={locationFilter} onChange={e => setLocationFilter(e.target.value)}>
                    <option value="">All locations</option>
                    {availableStates.map(st => <option key={st} value={st}>{st}</option>)}
                  </select>
                )}
              </div>
              <div className="flex-1 overflow-y-auto px-2 pb-2">
                {filtered.map(org => <CompactCard key={org.id} org={org} />)}
              </div>
            </>
          )}
          {activeTab === 'network' && (
            <div className="flex-1 overflow-y-auto p-4">
              <p className="text-[12px] text-muted-c text-center py-6">View your referral network in the main panel</p>
            </div>
          )}
        </div>
      </div>

      {/* ════════════ MOBILE ════════════
           Map pinned at the top of the scroller (sticky), the card panel
           slides up over it. Sticky, not fixed: a fixed element inside an
           overflow scroller is repositioned late on iOS and the whole page
           judders; sticky is handled by the compositor and stays smooth. */}
      <div className="md:hidden flex-1 min-h-0 overflow-y-auto overflow-x-hidden bg-primary overscroll-y-contain">
        <div className="dir-map-mobile sticky top-0 min-h-[250px] z-0">
          {loading
            ? <div className="h-full bg-tertiary flex items-center justify-center"><div className="w-8 h-8 border-2 border-strong border-t-accent rounded-full animate-spin" /></div>
            : mapElement
          }
        </div>

        {/* Content panel — overlaps the bottom of the map and scrolls over it */}
        <div className="relative z-[1] bg-primary rounded-t-[24px] -mt-6 min-h-full shadow-[0_-8px_30px_rgba(0,0,0,0.15)]">
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-1.5">
            <div className="w-10 h-[5px] rounded-full bg-strong/60" />
          </div>

          {/* Search + filters */}
          <div className="px-4 pb-2 sticky top-0 z-10 bg-primary">
            <div className="relative">
              <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-c" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              <input
                type="text"
                className="w-full pl-9 pr-9 py-2.5 bg-tertiary border border-strong rounded-full text-primary-c focus:outline-none focus:border-[#0DA7CA]"
                placeholder="Search by city, state, or zip..."
                value={search}
                onChange={e => handleSearchChange(e.target.value)}
                onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
              />
              {search && (
                <button onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-strong/50 text-muted-c flex items-center justify-center hover:bg-strong transition-all">
                  <X size={11} strokeWidth={2.5} />
                </button>
              )}
              {showSuggestions && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-elevated border border-theme rounded-xl shadow-2xl z-50 overflow-hidden">
                  {searchSuggestions.map(s => (
                    <button
                      key={s.id}
                      onClick={() => selectSearchLocation(s)}
                      className="w-full text-left flex items-center gap-2.5 px-4 py-2.5 hover:bg-hover-surface transition-all text-[13px] text-primary-c border-b border-theme last:border-0"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted-c flex-shrink-0"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      <span className="truncate">{s.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {searchLocation && (
              <div className="flex items-center gap-2 mt-2 px-1">
                <span className="text-[11px] text-accent-light font-medium">Within</span>
                <select value={searchRadius} onChange={e => setSearchRadius(Number(e.target.value))} className="text-[11px] font-semibold text-accent-light bg-transparent border-none focus:outline-none cursor-pointer">
                  {[5, 10, 15, 25, 50, 100].map(r => <option key={r} value={r}>{r} mi</option>)}
                </select>
                <span className="text-[11px] text-accent-light font-medium">of {searchLocation.label.split(',')[0]}</span>
                <button onClick={clearSearch} className="text-[10px] text-muted-c hover:text-primary-c ml-1">(clear)</button>
              </div>
            )}
            <div className="flex gap-1.5 mt-2.5 overflow-x-auto pb-2 scrollbar-none">
              <button
                onClick={() => { setNearMe(false); setTypeFilter(''); setLocationFilter(''); setProviderTypeFilter(''); }}
                className={`px-3.5 py-[7px] rounded-[10px] text-[12px] font-semibold whitespace-nowrap flex-shrink-0 transition-all ${
                  !nearMe && !typeFilter && !locationFilter && !providerTypeFilter ? 'bg-accent text-white shadow-sm' : 'bg-tertiary text-secondary-c border border-strong'
                }`}
              >
                All
              </button>
              {userLocation && (
                <button
                  onClick={() => { setNearMe(!nearMe); setLocationFilter(''); setTypeFilter(''); }}
                  className={`px-3.5 py-[7px] rounded-[10px] text-[12px] font-semibold whitespace-nowrap flex-shrink-0 transition-all flex items-center gap-1.5 ${
                    nearMe ? 'bg-accent text-white shadow-sm' : 'bg-tertiary text-secondary-c border border-strong'
                  }`}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg>
                  Near Me
                </button>
              )}
              <div className="w-px bg-strong flex-shrink-0 my-1" />
              {availableStates.map(st => (
                <button
                  key={st}
                  onClick={() => { setLocationFilter(locationFilter === st ? '' : st); setNearMe(false); setTypeFilter(''); }}
                  className={`px-3.5 py-[7px] rounded-[10px] text-[12px] font-semibold whitespace-nowrap flex-shrink-0 transition-all ${
                    locationFilter === st ? 'bg-accent text-white shadow-sm' : 'bg-tertiary text-secondary-c border border-strong'
                  }`}
                >
                  {st}
                </button>
              ))}
              {!isClientView && (
                <>
                  <div className="w-px bg-strong flex-shrink-0 my-1" />
                  {Object.entries(TYPE_LABELS).map(([k, v]) => (
                    <button
                      key={k}
                      onClick={() => { setTypeFilter(typeFilter === k ? '' : k); setNearMe(false); setLocationFilter(''); }}
                      className={`px-3.5 py-[7px] rounded-[10px] text-[12px] font-semibold whitespace-nowrap flex-shrink-0 transition-all ${
                        typeFilter === k ? 'bg-accent text-white shadow-sm' : 'bg-tertiary text-secondary-c border border-strong'
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </>
              )}
              {/* Provider-type chips — composable with state/near-me */}
              <div className="w-px bg-strong flex-shrink-0 my-1" />
              {PROVIDER_TYPES.map(pt => (
                <button
                  key={pt.value}
                  onClick={() => setProviderTypeFilter(providerTypeFilter === pt.value ? '' : pt.value)}
                  className={`px-3.5 py-[7px] rounded-[10px] text-[12px] font-semibold whitespace-nowrap flex-shrink-0 transition-all ${
                    providerTypeFilter === pt.value ? 'bg-accent text-white shadow-sm' : 'bg-tertiary text-secondary-c border border-strong'
                  }`}
                >
                  {pt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Count */}
          <div className="px-4 pb-2">
            <span className="text-[13px] font-semibold text-primary-c">{filtered.length} provider{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Cards */}
          <div className="px-4 pb-6 space-y-3">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="bg-elevated rounded-[16px] border border-theme overflow-hidden animate-pulse" style={{ animationDelay: `${i * 50}ms` }}>
                  <div className="h-1 bg-tertiary" />
                  <div className="p-4 flex gap-3.5">
                    <div className="w-14 h-14 rounded-[14px] bg-tertiary flex-shrink-0" />
                    <div className="flex-1 space-y-2 pt-1"><div className="h-4 bg-tertiary rounded w-3/4" /><div className="h-3 bg-tertiary rounded w-1/2" /></div>
                  </div>
                </div>
              ))
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-[14px] text-muted-c">No providers found</p>
                <p className="text-[12px] text-muted-c mt-1">Try a different search or filter</p>
              </div>
            ) : (
              filtered.map(org => <MobileCard key={org.id} org={org} />)
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
